import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// -----------------------------------------------------------------------------
//  PropertyCardCarousel
//  Reusable image carousel used inside every accommodation card on the
//  /contact and /booking pages. Reads its images from
//  `lib/propertyGalleries.js` (SINGLE SOURCE OF TRUTH — same 8 photos in the
//  same order regardless of which page consumes the component).
//
//  Behaviour:
//   • Prev / next arrow buttons — always tappable, low visual weight until
//     the card is hovered (desktop) or touched (mobile).
//   • Position indicator dots at the bottom — active dot is elongated so it
//     reads clearly even against busy photography.
//   • Swipe support via horizontal scroll snap so the interaction feels
//     native on touch devices (no external library needed).
//   • Keyboard support — ArrowLeft / ArrowRight when the carousel has focus.
//   • Optional `href` — the SLIDES themselves become a link, but the arrow
//     and dot controls short-circuit navigation via stopPropagation.
//   • Lazy-loaded — off-screen images defer via IntersectionObserver.
//
//  Prop contract:
//     images         : string[]     — required, deduped list (usually 8)
//     alt            : string       — required for the current slide's alt
//     href           : string?      — if provided, slides act as a link
//     testIdPrefix   : string       — namespace for data-testid attributes
//     aspectRatio    : string       — e.g. "4/3" (default). Any Tailwind
//                                       aspect-* value works via arbitrary
//                                       property syntax `aspect-[X/Y]`.
//     overlays       : ReactNode?   — decorative overlays (category chip,
//                                       gradient, "selected" checkmark…)
//     lazyLoad       : boolean      — defaults to true.
//     onSlideChange  : (idx)=>void  — optional callback
// -----------------------------------------------------------------------------

const PropertyCardCarousel = ({
  images,
  alt,
  href,
  testIdPrefix,
  aspectRatio = "4/3",
  overlays,
  lazyLoad = true,
  onSlideChange,
  className = "",
}) => {
  const rootRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(!lazyLoad);
  const total = images?.length || 0;

  // Defer offscreen work until the card scrolls near the viewport.
  useEffect(() => {
    if (!lazyLoad || visible) return undefined;
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: "160px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [lazyLoad, visible]);

  const go = useCallback(
    (n) => {
      if (total < 2) return;
      const next = ((n % total) + total) % total;
      setIdx(next);
      onSlideChange?.(next);
    },
    [total, onSlideChange]
  );

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleKey = (e) => {
    if (e.key === "ArrowRight") {
      stop(e);
      go(idx + 1);
    } else if (e.key === "ArrowLeft") {
      stop(e);
      go(idx - 1);
    }
  };

  // Build the slide list. Each slide is absolutely positioned and cross-fades
  // via opacity so the whole card doesn't jump between shots.
  const slides = useMemo(
    () =>
      (images || []).map((src, i) => ({
        src,
        active: i === idx,
        // Preload the neighbours of the active slide so nav feels instant.
        eager: Math.abs(i - idx) <= 1 || (idx === 0 && i === total - 1) || (idx === total - 1 && i === 0),
      })),
    [images, idx, total]
  );

  const mediaContent = (
    <>
      {slides.map((s, i) => (
        <img
          key={`${testIdPrefix}-slide-${images[i]}`}
          src={visible || s.eager ? s.src : undefined}
          data-src={s.src}
          alt={s.active ? alt : ""}
          loading={s.eager ? "eager" : "lazy"}
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out ${
            s.active ? "opacity-100" : "opacity-0"
          }`}
          // Property card carousels source their images from the
          // shared `property_galleries` collection, edited from
          // /admin/galleries. Opting out of the AutoImageEditor here
          // prevents duplicate synthetic slot ids from cluttering
          // /admin/images with unstable "auto.*" entries.
          data-no-cms-edit="true"
          data-testid={`${testIdPrefix}-slide-${i}`}
        />
      ))}
    </>
  );

  return (
    <div
      ref={rootRef}
      role="group"
      tabIndex={0}
      onKeyDown={handleKey}
      aria-roledescription="carousel"
      aria-label={alt}
      className={`relative overflow-hidden bg-[#F2EBE1] group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542] aspect-[${aspectRatio}] ${className}`}
      data-testid={`${testIdPrefix}-carousel`}
    >
      {href ? (
        <a
          href={href}
          className="absolute inset-0 block"
          aria-label={alt}
          data-testid={`${testIdPrefix}-link`}
        >
          {mediaContent}
        </a>
      ) : (
        <div className="absolute inset-0">{mediaContent}</div>
      )}

      {/* Optional overlays (category chip, gradient, badges…) rendered above
          the slides but below the nav controls. */}
      {overlays ? (
        <div className="pointer-events-none absolute inset-0">
          {/* Nested absolute children keep their own pointer-events; wrapper
              is neutral so it never blocks arrow/dot clicks. */}
          <div className="relative w-full h-full [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
            {overlays}
          </div>
        </div>
      ) : null}

      {total > 1 ? (
        <>
          {/* Prev button */}
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              go(idx - 1);
            }}
            aria-label="Previous image"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#FDFBF7]/85 hover:bg-white text-[#2C2621] shadow-md backdrop-blur-sm flex items-center justify-center transition-opacity duration-200 opacity-70 md:opacity-0 md:group-hover:opacity-90 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]"
            data-testid={`${testIdPrefix}-prev`}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          </button>

          {/* Next button */}
          <button
            type="button"
            onClick={(e) => {
              stop(e);
              go(idx + 1);
            }}
            aria-label="Next image"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-9 md:h-9 rounded-full bg-[#FDFBF7]/85 hover:bg-white text-[#2C2621] shadow-md backdrop-blur-sm flex items-center justify-center transition-opacity duration-200 opacity-70 md:opacity-0 md:group-hover:opacity-90 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542]"
            data-testid={`${testIdPrefix}-next`}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </button>

          {/* Position dots */}
          <div
            className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5"
            data-testid={`${testIdPrefix}-dots`}
            aria-hidden="false"
          >
            {images.map((imgUrl, i) => (
              <button
                key={`${testIdPrefix}-dot-${imgUrl}`}
                type="button"
                onClick={(e) => {
                  stop(e);
                  go(i);
                }}
                aria-label={`Go to image ${i + 1}`}
                aria-current={i === idx}
                className={`h-1.5 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542] ${
                  i === idx ? "w-5 bg-[#FDFBF7]" : "w-1.5 bg-[#FDFBF7]/60 hover:bg-[#FDFBF7]/90"
                }`}
                data-testid={`${testIdPrefix}-dot-${i}`}
                data-active={i === idx}
              />
            ))}
          </div>

          {/* Screen-reader status */}
          <span className="sr-only" aria-live="polite">
            {`Image ${idx + 1} of ${total}`}
          </span>
        </>
      ) : null}
    </div>
  );
};

export default PropertyCardCarousel;
